/**
 * Maps GET /api/v1/watchdogs/summary/* responses to legacy Issues UI shapes
 * (worldStore.watchDogs[].items + product map entries).
 */

/** camelCase watchdog key as used in IndexPage `wdList` → API snake_case `watchdog_type` */
export function watchdogTypeToApiParam (frontendKey) {
  if (!frontendKey) return 'lost_buybox'
  return String(frontendKey)
    .replace(/([A-Z])/g, '_$1')
    .replace(/^_/, '')
    .toLowerCase()
}

/**
 * Canonical API `watchdog_type` (snake_case from GET summary/alert_counts) → frontend key.
 * Matches `WatchdogTypes::WATCHDOG_TYPES` / Postman `watchdog_types[].watchdog_type`.
 */
var WATCHDOG_TYPE_API_TO_FRONTEND = {
  buybox: 'lostBuybox',
  lost_buybox: 'lostBuybox',
  number_of_sellers: 'numberOfSellers',
  listing_suspended: 'listingSuspended',
  no_add_to_cart: 'noAddToCart',
  sales_drop: 'salesDrop',
  keyword: 'keyword',
  bad_review: 'badReview',
  top_review: 'topReview',
  image_missing: 'imageMissing',
  star_rating_recent_change: 'starRatingRecentChange',
  product_details_recent_change: 'productDetailsRecentChange',
  product_category_recent_change: 'productCategoryRecentChange',
  best_seller_badge: 'bestSellerBadge',
  bad_seller_feedback: 'badSellerFeedback',
  price: 'price',
  search_ban: 'searchBan',
  inventory: 'inventory',
  fbm_inventory: 'fbmInventory',
  daily_fixed_limits: 'dailyFixedLimits',
  daily_limits_rolling_avg: 'dailyLimitsRollingAvg',
  /** Backend splits “sales volume” style alerts; Issues sidebar / wdList use `salesDrop` only. */
  hours_without_sales: 'salesDrop',
  bsr_limits: 'bsrLimits',
  review_count_drop: 'reviewCountDrop',
  fba_fee_change: 'fbaFeeChange',
  competitor_price: 'competitorPrice',
  competitive_price: 'competitivePrice',
  review_health: 'reviewHealth',
  prime: 'prime',
  amazon_choice_badge: 'amazonChoiceBadge',
  subscribe_and_save: 'subscribeAndSave',
  parent_product_change: 'parentProductChange',
  adult_classification: 'adultClassification'
}

/** API response `watchdog_type` → frontend wdList / UI key */
export function watchdogTypeFromApi (apiKey) {
  if (apiKey == null || apiKey === '') return 'lostBuybox'
  var s = String(apiKey).trim()
  var direct = WATCHDOG_TYPE_API_TO_FRONTEND[s]
  if (direct) return direct
  var lower = s.toLowerCase()
  var byLower = WATCHDOG_TYPE_API_TO_FRONTEND[lower]
  if (byLower) return byLower
  return s.replace(/_([a-z])/g, function (_, c) {
    return c.toUpperCase()
  })
}

/** Parsed JSON from vue-resource `response` (body / data / string). */
export function parseSummaryJsonPayload (response) {
  if (!response || typeof response !== 'object') return {}
  var raw = response.body
  if (raw === undefined || raw === null || raw === '') raw = response.data
  if (raw === undefined || raw === null || raw === '') raw = response.jsonBody
  if (typeof raw === 'string') {
    var s = raw.trim()
    if (!s) return {}
    try {
      raw = JSON.parse(s)
    } catch (e) {
      return {}
    }
  }
  return typeof raw === 'object' && raw !== null ? raw : {}
}

/** Alias for alert_counts responses (same unwrap rules). */
export function parseAlertCountsHttpPayload (response) {
  return parseSummaryJsonPayload(response)
}

/**
 * Maps Issues navbar `dateRange` → GET summary `time_range` / `timeRange`.
 * Backend default is `all_time`; omit sending only when equivalent to all time.
 */
export function issuesUiDateRangeToApiTimeRange (uiRange) {
  var r = uiRange == null ? '' : String(uiRange).trim().toLowerCase().replace(/-/g, '_').replace(/\s+/g, '_')
  if (r === 'all' || r === 'all_time' || r === 'alltime' || r === '') return 'all_time'
  if (r === 'today') return 'today'
  if (r === '7d' || r === 'last_7_days' || r === 'last7days') return 'last_7_days'
  if (r === '30d' || r === 'last_30_days' || r === '30_days') return 'last_30_days'
  if (r === '90d' || r === 'last_90_days' || r === '90_days') return 'last_90_days'
  if (r === 'custom') return 'custom'
  return 'all_time'
}

/**
 * @param {{ start: string, end: string }} [customRange] - YYYY-MM-DD bounds, required when uiDateRange is 'custom'.
 */
export function buildSummaryRequestParams (filterByMarket, filterBySellAcc, uiDateRange, customRange) {
  var params = {}
  if (filterByMarket && filterByMarket.length > 0) {
    params.marketplaces = filterByMarket.map(function (m) {
      return String(m).toUpperCase()
    }).join(',')
  }
  if (filterBySellAcc && filterBySellAcc.length > 0) {
    params.seller_ids = filterBySellAcc.map(function (id) {
      var n = Number(id)
      return isNaN(n) ? String(id) : n
    }).join(',')
  }
  var timeRange = issuesUiDateRangeToApiTimeRange(uiDateRange)
  if (timeRange === 'custom') {
    if (customRange && customRange.start && customRange.end) {
      params.start_date = customRange.start
      params.end_date = customRange.end
    } else {
      timeRange = 'all_time'
    }
  }
  params.time_range = timeRange
  return params
}

function mapIssueStatusToNumber (raw) {
  if (raw === undefined || raw === null) return 0
  if (typeof raw === 'number' && !isNaN(raw)) return raw
  var s = String(raw).toLowerCase()
  if (s === 'seen') return 1
  if (s === 'resolved') return 3
  return 0
}

function mapApiStatusToNumeric (status) {
  if (status === undefined || status === null) return 0
  if (typeof status === 'number') return status
  var s = String(status).toLowerCase()
  if (s === 'red') return 2
  if (s === 'yellow' || s === 'amber') return 1
  if (s === 'green') return 99
  return 0
}

function syntheticLostBuyboxMessage (details) {
  if (!details || typeof details !== 'object') return ''
  if (details.lost_to_amazon) return 'Lost to Amazon'
  if (details.lost_to_whitelisted_seller) return 'Lost to whitelisted seller'
  if (details.lost_to_another_seller) return 'Lost to another seller'
  if (details.has_own_offer) return 'Buy Box rotating'
  return ''
}

function isLikelyHttpImageUrl (u) {
  if (u == null || u === '') return false
  return /^https?:\/\//i.test(String(u).trim())
}

function normalizeProductForStore (product, frontendWdType) {
  if (!product || typeof product !== 'object') return null
  /** Summary/items `product` uses `productId` (SOTW stock entry); older payloads used `id`. */
  var rawId = product.id != null ? product.id : product.productId
  var id = rawId != null ? String(rawId) : null
  if (!id) return null
  var rawTitle = product.name || product.productTitle || ''
  var asinStr = product.asin ? String(product.asin).trim() : ''
  var title = rawTitle ? String(rawTitle).trim() : ''
  if (!title && asinStr) title = asinStr
  var imgPick =
    product.large_image_url ||
    product.largeImageUrl ||
    product.image_url ||
    product.imageUrl ||
    null
  var img = imgPick != null && isLikelyHttpImageUrl(imgPick) ? String(imgPick).trim() : null
  var sellerFromProduct = product.seller_id != null ? product.seller_id : product.sellerId
  var row = {
    productId: id,
    productTitle: title,
    largeImageUrl: img,
    imageUrl: img,
    asin: product.asin || '',
    marketplace: product.marketplace || '',
    seller_key: product.seller_key,
    seller_id: sellerFromProduct,
    productUrl: product.productUrl || product.product_url || ''
  }
  if (frontendWdType === 'badReview' && product.productReviewsUrl) row.productReviewsUrl = product.productReviewsUrl
  if (frontendWdType === 'topReview' && product.productTopReviewsUrl) row.productTopReviewsUrl = product.productTopReviewsUrl
  return row
}

/**
 * One summary row → legacy issue item + optional product map entry.
 * @param {boolean} [fromResolvedList] - row came from API `resolved[]` (already cleared — hide resolve actions, label as resolved).
 */
export function normalizeSummaryWatchdogRow (row, frontendWdType, fromResolvedList) {
  if (!row || typeof row !== 'object') return { item: null, productEntry: null }

  var wid = row.watchdog_id != null ? row.watchdog_id : row.watchDogId
  var key = wid != null ? String(wid) : null

  var statusDetails = row.statusDetails
  if (statusDetails == null && row.status_details != null) statusDetails = row.status_details
  if (!statusDetails || typeof statusDetails !== 'object') statusDetails = {}

  var productRaw = row.product
  var productEntry = normalizeProductForStore(productRaw, frontendWdType)
  var productIdFromRow =
    row.productId != null
      ? String(row.productId)
      : row.product_id != null
        ? String(row.product_id)
        : null
  var productId = productEntry ? productEntry.productId : productIdFromRow

  var numericStatus = mapApiStatusToNumeric(row.status)
  var msg = typeof statusDetails.message === 'string' ? statusDetails.message : ''
  if (!msg && frontendWdType === 'lostBuybox') {
    msg = syntheticLostBuyboxMessage(statusDetails)
  }

  var statusDetailsOut = Object.assign({}, statusDetails)
  if (msg && !statusDetailsOut.message) statusDetailsOut.message = msg

  var item = {
    watchDogItemId: key || ('wd_' + Math.random().toString(36).slice(2)),
    watchDogId: wid,
    productId: productId,
    status: numericStatus,
    issueStatus: fromResolvedList ? 3 : mapIssueStatusToNumber(row.issue_status != null ? row.issue_status : row.issueStatus),
    muted: !!row.muted,
    date: row.status_change_timestamp || row.date || row.updated_at || null,
    marketplace: row.marketplace || (productRaw && productRaw.marketplace) || null,
    sellerId: row.seller_id != null ? row.seller_id : row.sellerId,
    statusDetails: statusDetailsOut,
    title: productEntry ? productEntry.productTitle : '',
    asin:
      productRaw && productRaw.asin
        ? String(productRaw.asin)
        : ''
  }

  if ((!item.title || !String(item.title).trim()) && productIdFromRow && item.asin) {
    item.title = String(item.asin).trim()
  }

  var origKeys = Object.keys(row)
  for (var k = 0; k < origKeys.length; k++) {
    var ok = origKeys[k]
    if (item[ok] === undefined) item[ok] = row[ok]
  }

  return { item: item, productEntry: productEntry ? { id: productEntry.productId, row: productEntry } : null }
}

export function summaryItemsToLegacyMaps (body, frontendWdType) {
  var itemsObj = {}
  var productsPatch = {}

  function ingestArray (arr, fromResolvedList) {
    if (!Array.isArray(arr)) return
    for (var i = 0; i < arr.length; i++) {
      var pair = normalizeSummaryWatchdogRow(arr[i], frontendWdType, fromResolvedList)
      if (!pair.item) continue
      var kid = pair.item.watchDogItemId
      itemsObj[kid] = pair.item
      if (pair.productEntry) productsPatch[pair.productEntry.id] = pair.productEntry.row
    }
  }

  if (!body || typeof body !== 'object') {
    return { items: itemsObj, productsPatch: productsPatch, summaryTotals: null }
  }

  var active = body.active || {}
  ingestArray(active.red, false)
  ingestArray(active.yellow, false)
  ingestArray(body.resolved, true)

  var summaryTotals = null
  var meta = body.meta
  if (meta && meta.totals && typeof meta.totals === 'object') {
    var t = meta.totals
    summaryTotals = {
      activeRed: Number(t.active_red) || 0,
      activeYellow: Number(t.active_yellow) || 0,
      resolved: Number(t.resolved) || 0
    }
  }

  return { items: itemsObj, productsPatch: productsPatch, summaryTotals: summaryTotals }
}

/**
 * Normalize GET /api/v1/watchdogs/summary/period_changes for billing Usage tab.
 */
export function parsePeriodChangesBody (body) {
  if (!body || typeof body !== 'object') {
    return {
      periodStart: null,
      periodEnd: null,
      connectedStorefrontsCount: null,
      activeListedAsinsCount: null,
      watchdogChangesCount: null,
      checksRunCount: null,
      marketplacesCoveredCount: null,
      categories: [],
      total: null,
      filters: null
    }
  }
  var categories = body.categories
  if (!Array.isArray(categories)) categories = []
  return {
    periodStart: body.period_start != null ? body.period_start : body.periodStart,
    periodEnd: body.period_end != null ? body.period_end : body.periodEnd,
    connectedStorefrontsCount:
      body.connected_storefronts_count != null
        ? body.connected_storefronts_count
        : body.connectedStorefrontsCount,
    activeListedAsinsCount:
      body.active_listed_asins_count != null
        ? body.active_listed_asins_count
        : body.activeListedAsinsCount,
    watchdogChangesCount:
      body.watchdog_changes_count != null
        ? body.watchdog_changes_count
        : body.watchdogChangesCount,
    checksRunCount:
      body.checks_run_count != null ? body.checks_run_count : body.checksRunCount,
    listingsWatchedCount:
      body.listings_watched_count != null ? body.listings_watched_count : body.listingsWatchedCount,
    marketplacesCoveredCount:
      body.marketplaces_covered_count != null
        ? body.marketplaces_covered_count
        : body.marketplacesCoveredCount,
    categories: categories.map(function (row) {
      if (!row || typeof row !== 'object') return { key: '', label: '', count: 0 }
      return {
        key: row.key != null ? String(row.key) : '',
        label: row.label != null ? String(row.label) : '',
        count: Number(row.count) || 0
      }
    }),
    estimatedImpactProtectedCents:
      body.estimated_impact_protected_cents != null
        ? body.estimated_impact_protected_cents
        : body.estimatedImpactProtectedCents,
    total: body.total != null ? body.total : null,
    filters: body.filters || null
  }
}

/** Bar width for period-change category rows (0–100%). */
export function periodChangesCategoryBarWidth (count, maxCount) {
  var n = Number(count)
  var max = Number(maxCount)
  if (isNaN(n) || n <= 0 || isNaN(max) || max <= 0) return '0%'
  return Math.max(4, Math.min(100, Math.round((n / max) * 100))) + '%'
}

/**
 * alert_counts body → { [frontendWdType]: { red, yellow } }.
 * When `wdListSet` is omitted or has no keys, every API row is kept (sidebar + totals).
 * When `wdListSet` has keys, only those frontend types are kept.
 */
export function parseAlertCountsBody (body, wdListSet) {
  var out = {}
  var rows =
    (body && body.watchdog_types) ||
    (body && body.watchdogTypes) ||
    []
  var restrict =
    wdListSet &&
    typeof wdListSet === 'object' &&
    Object.keys(wdListSet).length > 0
  for (var i = 0; i < rows.length; i++) {
    var row = rows[i]
    if (!row || typeof row !== 'object') continue
    /** Prefer snake_case `watchdog_type` from alert_counts (see API contract). */
    var wt = row.watchdog_type != null ? row.watchdog_type : row.watchdogType
    if (wt == null || wt === '') continue
    wt = String(wt).trim()
    var fk = watchdogTypeFromApi(wt)
    if (restrict && !wdListSet[fk]) continue
    var red = Number(row.red) || 0
    var yellow = Number(row.yellow) || 0
    if (!out[fk]) out[fk] = { red: 0, yellow: 0 }
    out[fk].red += red
    out[fk].yellow += yellow
  }
  return out
}



// WEBPACK FOOTER //
// ./src/utils/watchdogSummaryApi.js